// src/pages/MyProposals.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "react-i18next";

const STATUS_COLORS = {
  paid: "bg-green-100 text-green-800",
  pending: "bg-amber-100 text-amber-800",
  awaiting_payment: "bg-amber-100 text-amber-800",
  cancelled: "bg-rose-100 text-rose-800",
  canceled: "bg-rose-100 text-rose-800",
  rejected: "bg-rose-100 text-rose-800",
  sent: "bg-blue-100 text-blue-800",
  in_progress: "bg-blue-100 text-blue-800",
  completed: "bg-green-100 text-green-800",
  draft: "bg-gray-100 text-gray-700",
};

const FILTERS = ["all", "draft", "pending", "sent", "paid"];

export default function MyProposals() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    let ignore = false;
    const run = async () => {
      if (!user?.id) return;
      setLoading(true);
      setLoadError(false);

      const { data, error } = await supabase
        .from("proposals")
        .select("id, artwork_id, institution, proposal_type, send_method, status, created_at")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (!ignore) {
        if (error) {
          console.error("MyProposals fetch error:", error);
          setRows([]);
          setLoadError(true);
        } else {
          setRows(data || []);
        }
        setLoading(false);
      }
    };
    run();
    return () => { ignore = true; };
  }, [user?.id]);

  const fdate = (iso) => {
    if (!iso) return "";
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return "";
    return new Intl.DateTimeFormat(i18n.resolvedLanguage || i18n.language || undefined, {
      day: "2-digit", month: "short", year: "numeric",
    }).format(date);
  };

  const bucket = (status) => (status === "awaiting_payment" ? "pending" : status);

  const filtered = useMemo(
    () => (filter === "all" ? rows : rows.filter((r) => bucket(r.status) === filter)),
    [rows, filter]
  );

  const counts = useMemo(() => {
    const c = { all: rows.length, draft: 0, pending: 0, sent: 0, paid: 0 };
    rows.forEach((r) => {
      const b = bucket(r.status);
      if (c[b] !== undefined) c[b] += 1;
    });
    return c;
  }, [rows]);

  const getTypeLabel = (proposalType) =>
    t(`mesDemandes.type.${proposalType || "unknown"}`, { defaultValue: t("mesDemandes.type.unknown") });

  const getSendMethodLabel = (sendMethod) =>
    t(`mesDemandes.sendMethod.${sendMethod || "unknown"}`, { defaultValue: t("mesDemandes.sendMethod.unknown") });

  const gotoItem = (row) =>
    navigate(`/propose/${row.artwork_id}`, {
      state: {
        proposalId: row.id,
        artworkId: row.artwork_id,
        institution: row.institution,
        proposalType: row.proposal_type,
      },
    });

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <button
        onClick={() => navigate("/my-collection")}
        className="text-sm text-gray-600 hover:text-gray-900 mb-4"
      >
        {t("mesDemandes.backDashboard")}
      </button>

      <h1 className="text-2xl md:text-3xl font-light text-gray-900 mb-1" style={{ fontFamily: "Georgia, serif" }}>
        {t("mesDemandes.listTitle")}
      </h1>
      <p className="text-sm text-gray-500 mb-6">{t("mesDemandes.listSubtitle")}</p>

      <div className="flex flex-wrap gap-2 mb-6">
        {FILTERS.map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-full text-sm border ${
              filter === f ? "bg-black text-white border-black" : "bg-white border-gray-200 hover:bg-gray-50"
            }`}
          >
            {f === "all" ? t("mesDemandes.filters.all") : t(`mesDemandes.status.${f}`)} ({counts[f] ?? 0})
          </button>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-[#e8e3d8] p-6">
        {loading ? (
          <div className="text-sm text-gray-500">{t("mesDemandes.loading")}</div>
        ) : loadError ? (
          <div className="text-sm text-red-600" role="alert">{t("mesDemandes.error")}</div>
        ) : filtered.length === 0 ? (
          <div className="text-sm text-gray-500">{t("mesDemandes.empty")}</div>
        ) : (
          <div className="divide-y">
            {filtered.map((r) => {
              const statusKey = String(r.status || "unknown");
              const label = t(`mesDemandes.status.${statusKey}`, {
                defaultValue: t("mesDemandes.status.unknown", { status: r.status || "—" }),
              });
              const cls = STATUS_COLORS[r.status] || "bg-gray-100 text-gray-700";

              return (
                <div key={r.id} className="py-3 flex items-center justify-between">
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">
                      {r.institution || t("mesDemandes.institutionFallback")}
                    </div>
                    <div className="text-xs text-gray-500">
                      {fdate(r.created_at)} • {getTypeLabel(r.proposal_type)} • {getSendMethodLabel(r.send_method)}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>
                      {label}
                    </span>
                    <button
                      type="button"
                      onClick={() => gotoItem(r)}
                      className="text-sm px-2.5 py-1 border rounded hover:bg-gray-50"
                    >
                      {t("mesDemandes.view.label")}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
